import { type FC, useMemo } from "react";

import { cx } from "@emotion/css";
import { AutoSizer, Spacing, Tag } from "@snapchat/snap-design-system";

import { getKeys } from "../../../shared/utils/object";
import { useBreakdownDimension, useDimensionBreakdownFunnel } from "../../hooks";
import { Dimension, dimensionLabelsMap, displayDimensionsMap } from "../../models/dimension-funnel";
import { ChartSkeletonContainer } from "../ChartSkeletonContainer";
import { ChartPaper, StatsEmptyStateContainer } from "../shared";

import { EngagementBreakdownHeader } from "./EngagementBreakdownHeader";
import { createTable } from "./engagementBreakdownTable";
import { Container, TagsContainer, tag, tagDisabled, tagSelected } from "./funnelTab.styles";
import { makeDimensionLabels } from "./makeDimensionLabels";

export const EngagementBreakdownSection: FC = () => {
    const { breakdownDimension, setBreakdownDimension } = useBreakdownDimension();
    const { loading, isEmpty, dimensionFunnelMap } = useDimensionBreakdownFunnel();

    const dimensionLabels = useMemo(() => {
        return breakdownDimension === Dimension.ALL_USERS
            ? getKeys(dimensionLabelsMap).flatMap((dimension: Dimension) =>
                  makeDimensionLabels(dimension, dimensionFunnelMap),
              )
            : [
                  ...makeDimensionLabels(Dimension.TOTAL, dimensionFunnelMap),
                  ...makeDimensionLabels(breakdownDimension, dimensionFunnelMap),
              ];
    }, [breakdownDimension, dimensionFunnelMap]);

    const tableCreator = useMemo(
        () => createTable(dimensionFunnelMap, dimensionLabels),
        [dimensionFunnelMap, dimensionLabels],
    );

    return (
        <Container>
            <EngagementBreakdownHeader />

            <ChartPaper padding={`${Spacing.XL}px ${Spacing.LG}px`} flexDirection="column">
                <TagsContainer>
                    {Object.entries(displayDimensionsMap)
                        .filter(([_, value]) => !!value)
                        .map(([key, value]) => (
                            <Tag
                                key={key}
                                className={cx(
                                    tag,
                                    !isEmpty && (key as Dimension) === breakdownDimension && tagSelected,
                                    isEmpty && tagDisabled,
                                )}
                                onClick={() => {
                                    setBreakdownDimension(key as Dimension);
                                }}
                            >
                                {value}
                            </Tag>
                        ))}
                </TagsContainer>

                <ChartSkeletonContainer skeletonActive={loading}>
                    <StatsEmptyStateContainer isEmpty={isEmpty}>
                        <AutoSizer disableHeight>{tableCreator}</AutoSizer>
                    </StatsEmptyStateContainer>
                </ChartSkeletonContainer>
            </ChartPaper>
        </Container>
    );
};
